// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  LabelList,
  Customized,
} from 'recharts';
import { ArrowsRightLeftIcon, DocumentIcon } from '@heroicons/react/24/solid';
import { formatDate } from 'shared_items/formatConventions';
import { fetchFlowsConsolidated } from '../../moduleServices/flow_requests';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import { useFinanceProfile } from '../../moduleServices/finance_profilecontext';

// ###### CUSTOM COMPONENTS
// Custom shape for waterfall bars
const CustomBarShape = ({ x, y, width, height, payload, fill }) => {
  if (height <= 0) return null;

  let radius = 8;
  const isDiff = payload.name === 'Diff.';
  const right = x + width;
  const bottom = y + height;

  if (height < radius * 2) {
    radius = height / 2;
  }

  let path = '';

  if (isDiff) {
    path = `
      M${x},${y + radius}
      A${radius},${radius} 0 0 1 ${x + radius},${y}
      L${right - radius},${y}
      A${radius},${radius} 0 0 1 ${right},${y + radius}
      L${right},${bottom - radius}
      A${radius},${radius} 0 0 1 ${right - radius},${bottom}
      L${x + radius},${bottom}
      A${radius},${radius} 0 0 1 ${x},${bottom - radius}
      Z
    `;
  } else {
    path = `
      M${x},${y + radius}
      A${radius},${radius} 0 0 1 ${x + radius},${y}
      L${right - radius},${y}
      A${radius},${radius} 0 0 1 ${right},${y + radius}
      L${right},${bottom}
      L${x},${bottom}
      Z
    `;
  }

  return <path d={path} fill={fill} />;
};

// Waterfall connecting lines
const WaterfallLines = ({ yAxisMap, xAxisMap, offset, inValue, outValue, diffBase, diffValue }) => {
  const yScale = yAxisMap[0].scale;
  const xScale = xAxisMap[0].scale;

  const inX = xScale('In') + offset.left + xScale.bandwidth() / 2;
  const outX = xScale('Out') + offset.left + xScale.bandwidth() / 2;
  const diffX = xScale('Diff.') + offset.left + xScale.bandwidth() / 2;

  const inTop = yScale(inValue);
  const outTop = yScale(outValue);
  const diffTop = yScale(diffBase + diffValue);
  const diffBaseY = yScale(diffBase);

  const isInGreater = inValue > outValue;

  return (
    <g>
      <line
        x1={inX}
        y1={inTop}
        x2={diffX}
        y2={isInGreater ? diffTop : diffBaseY}
        stroke="#bbb"
        strokeDasharray="4 2"
        strokeWidth={1}
      />
      <line
        x1={outX}
        y1={outTop}
        x2={diffX}
        y2={isInGreater ? diffBaseY : diffTop}
        stroke="#999"
        strokeDasharray="4 2"
        strokeWidth={1}
      />
    </g>
  );
};

// ###### UTILS
const formatValue = (value) => {
  return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(1);
};

// ###### MAIN COMPONENT
const CardOverviewFlow = ({ onNavigateToFlow, selectedFlowCategL0: propSelectedFlowCategL0 }) => {
  const { profile } = useFinanceProfile();
  const defaultCategL0 = profile?.default_categ_l0 || 'All';
  const selectedFlowCategL0 = propSelectedFlowCategL0 || defaultCategL0;
  const [flowsConsolidated, setFlowsConsolidated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ### HANDLERS
  const handleCardClick = () => {
    if (onNavigateToFlow) {
      onNavigateToFlow();
    }
  };

  // Load data
  useEffect(() => {
    const loadConsolidated = async () => {
      try {
        setLoading(true);
        const response = await fetchFlowsConsolidated();
        setFlowsConsolidated(response.data);
      } catch (err) {
        console.error('Error loading consolidated flows:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadConsolidated();
  }, []);

  if (loading) {
    return (
      <div className="bg-white shadow rounded-xl overflow-hidden">
        {/* Header with title - following VirtualizedFlowCards pattern */}
        <div className="bg-green-800 border-b border-gray-200">
          <div className="px-3 py-1 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <ArrowsRightLeftIcon className="h-5 w-5 text-white" />
              <h2 className="text-base font-semibold text-white capitalize">
                Flow
              </h2>
            </div>
            
            {/* Right side - last update info */}
            <div className="flex items-center gap-2">
              <div className="text-xs text-white opacity-70">
                Loading...
              </div>
            </div>
          </div>
        </div>

        {/* Content area with skeleton and spinner */}
        <div className="h-48 p-3 relative">
          {/* Skeleton content */}
          <div className="flex h-full items-center">
            {/* Left side skeleton */}
            <div className="w-24 mr-4">
              <div className="space-y-3">
                <div className="h-3 bg-gray-400 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-400 rounded animate-pulse"></div>
                <div className="h-3 bg-gray-400 rounded animate-pulse"></div>
                <div className="h-4 bg-gray-400 rounded animate-pulse"></div>
              </div>
            </div>
            
            {/* Right side skeleton */}
            <div className="flex-1 h-full flex items-end justify-around">
              <div className="w-12 bg-gray-400 rounded-t animate-pulse" style={{ height: '60px' }}></div>
              <div className="w-12 bg-gray-400 rounded-t animate-pulse" style={{ height: '40px' }}></div>
              <div className="w-12 bg-gray-400 rounded-t animate-pulse" style={{ height: '80px' }}></div>
            </div>
          </div>
          
          {/* Spinner overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
            <SpinnerLoad />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-xl overflow-hidden">
        {/* Header with title - following VirtualizedFlowCards pattern */}
        <div className="bg-gray-600 border-b border-gray-200">
          <div className="px-3 py-2 flex justify-between items-center">
            <h2 className="text-base font-semibold text-white capitalize">
              Flow
            </h2>
            
            {/* Right side - error info */}
            <div className="flex items-center gap-2">
              <div className="text-xs text-white opacity-70">
                Error
              </div>
            </div>
          </div>
        </div>

        {/* Content area with error message */}
        <div className="h-48 p-3 flex items-center justify-center">
          <div className="text-red-500 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  // Prepare chart data
  let currentFlow;
  if (selectedFlowCategL0 === 'All' && flowsConsolidated?.flows_categ_l0) {
    let totalIn = 0;
    let totalOut = 0;
    Object.values(flowsConsolidated.flows_categ_l0).forEach(val => {
      totalIn += Number(val.In) || 0;
      totalOut += Number(val.Out) || 0;
    });
    currentFlow = { In: totalIn, Out: totalOut };
  } else {
    currentFlow = flowsConsolidated?.flows_categ_l0?.[selectedFlowCategL0] || { In: 0, Out: 0 };
  }
  
  const inValue = currentFlow.In;
  const outValue = currentFlow.Out * -1;
  const diff = Number(Math.abs(inValue - outValue).toFixed(2));
  const diffBase = Math.min(inValue, outValue);
  const isInGreater = inValue > outValue;
  const diffColor = isInGreater ? '#AFDC7E' : '#FFA3A3';
  
  const data = [
    { name: 'In', value: inValue, base: 0, color: '#166534' },
    { name: 'Out', value: outValue, base: 0, color: '#C00000' },
    { name: 'Diff.', value: diff, base: diffBase, color: diffColor }
  ];

  const displayedFirstDate = flowsConsolidated?.flows_first_date || '-';
  const displayedLastDate = flowsConsolidated?.flows_last_date || '-';
  const displayedQtyDays = flowsConsolidated?.flows_qty_days != null ? flowsConsolidated?.flows_qty_days : '-';

  // Check if there's data to show
  const hasData = inValue !== 0 || outValue !== 0;

  return (
    <div 
      className="bg-white shadow rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-200"
      onClick={handleCardClick}
    >
      {/* Header with title - following VirtualizedFlowCards pattern */}
      <div className="bg-green-800 border-b border-gray-200">
        <div className="px-3 py-1 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ArrowsRightLeftIcon className="h-5 w-5 text-white" />
            <h2 className="text-base font-semibold text-white capitalize">
              Flow
            </h2>
          </div>
          
          {/* Right side - last update info */}
          <div className="flex items-center gap-2">
            <div className="text-xs text-white opacity-70">
              last update: {displayedLastDate && displayedLastDate !== '-' ? formatDate(displayedLastDate, 'display') : (displayedLastDate || '—')}
            </div>
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="h-48 p-3 pb-2">
        {hasData ? (
          <div className="flex h-full items-center">
            <div className="flex flex-col justify-center mr-4 w-24 cursor-pointer">
              <div className="mb-2">
                <div className="text-xs text-gray-500">Group</div>
                <div className="text-sm font-semibold capitalize">{selectedFlowCategL0}</div>
              </div>
              <div className="mb-2">
                <div className="text-xs text-gray-500">First</div>
                <div className="text-sm font-semibold">{displayedFirstDate && displayedFirstDate !== '-' ? formatDate(displayedFirstDate, 'display') : (displayedFirstDate || '—')}</div>
              </div>
              <div className="mb-2">
                <div className="text-xs text-gray-500">Last</div>
                <div className="text-sm font-semibold">{displayedLastDate && displayedLastDate !== '-' ? formatDate(displayedLastDate, 'display') : (displayedLastDate || '—')}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Qty Days</div>
                <div className="text-sm font-semibold">{displayedQtyDays}</div>
              </div>
            </div>
            <div className="flex-1 h-full cursor-pointer">
              <ResponsiveContainer width="100%" height="100%" style={{ cursor: 'pointer' }}>
                <BarChart 
                  data={data} 
                  margin={{ top: 15, right: 20, left: 0, bottom: 5 }}
                  style={{ cursor: 'pointer' }}
                >
                  <XAxis dataKey="name" tick={{ fontSize: 14, fontWeight: 'bold', fill: '#444' }} />
                  <YAxis hide />
                  <Customized
                    component={(props) => (
                      <WaterfallLines
                        {...props}
                        inValue={inValue}
                        outValue={outValue}
                        diffBase={diffBase}
                        diffValue={diff}
                      />
                    )}
                  />
                  <Bar dataKey="base" stackId="a" fill="transparent" />
                  <Bar dataKey="value" stackId="a" shape={(props) => <CustomBarShape {...props} />}>
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                    <LabelList
                      dataKey="value"
                      position="top"
                      fontSize={14}
                      fill="#444"
                      fontWeight="bold"
                      formatter={formatValue}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-400 mb-3">
                <DocumentIcon className="w-16 h-16 mx-auto" />
              </div>
              <div className="text-gray-500 text-sm font-medium mb-2">
                No flow data to show
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardOverviewFlow;
